import { Section } from "@/components/Section/Section";

import styles from "./HeaderBase.module.scss";
import { cloneElement } from "react";

type BaseHeaderProps = {
  className?: string;
  contentWrapperClassName?: string;
  headerFrame: "narrow-frame" | "normal-frame" | "frameless";
  contentFrame: "narrow-frame" | "normal-frame" | "frameless";
  header?: React.ReactElement;
  content?: React.ReactElement;
  height: "screen-height" | "content-height" | "custom-height";
  contentHorizontalAlignment: AligmentTypes;
  contentVerticalAlignment?: AligmentTypes;
  narrowFrameColumns?: string;
  max_height?: number;
};

export type AligmentTypes = "start" | "center" | "end" | "stretch";

type NonStackedHeaderProps = BaseHeaderProps & {
  arrangement?: "content-top" | "content-bottom";
};

type StackedHeaderProps = BaseHeaderProps & {
  arrangement: "overlay";
  heightDependency: "content" | "header";
  gradient: boolean;
};

type HeaderBaseProps = NonStackedHeaderProps | StackedHeaderProps;

const HeaderBase = ({
  className,
  contentWrapperClassName = "",
  headerFrame,
  contentFrame,
  content,
  header,
  height,
  contentHorizontalAlignment,
  contentVerticalAlignment = "center",
  narrowFrameColumns = "is-8",
  max_height,
  ...props
}: HeaderBaseProps) => {
  const { arrangement } = props;
  const arrange = (contentElements?: React.ReactElement) => {
    switch (arrangement) {
      case "content-top":
        return (
          <>
            {content && contentElements}
            {header}
          </>
        );
      case "content-bottom":
      case undefined:
        return (
          <>
            {header}
            {content && contentElements}
          </>
        );
      case "overlay":
        const { heightDependency, gradient } = props;
        const gradientJSX = gradient ? (
          <div className={styles["gradient"]} />
        ) : null;
        if (heightDependency === "header") {
          return (
            <>
              {header}
              {gradientJSX}
              {content && contentElements && (
                <>
                  {cloneElement(contentElements, {
                    className: styles["absolute"],
                    disableClip: true,
                  })}
                </>
              )}
            </>
          );
        }
        if (heightDependency === "content") {
          return (
            <>
              <div
                className={`${styles["absolute"]} ${styles["absolute-header"]}`}
              >
                {header}
              </div>
              {gradientJSX}
              {content && contentElements}
            </>
          );
        }
    }
    return <></>;
  };
  //section-padding-y min-height-acceptor

  const columns = `is-gapless is-justify-content-${contentHorizontalAlignment} is-mobile`;
  const verticalAlignmentContainer = `is-flex is-flex-direction-column is-justify-content-${contentVerticalAlignment} min-height-acceptor`;

  let headerAndContent = <></>;

  if (headerFrame === "frameless" && contentFrame === "frameless") {
    headerAndContent = arrange(content);
  }

  if (headerFrame === "frameless" && contentFrame === "normal-frame") {
    headerAndContent = arrange(
      <Section
        className={`py-0 ${contentWrapperClassName}`}
        disableColumns
        containerClassName={verticalAlignmentContainer}
        disableClip
      >
        {content}
      </Section>,
    );
  }

  if (headerFrame === "frameless" && contentFrame === "narrow-frame") {
    headerAndContent = arrange(
      <Section
        columnsClassName={columns}
        tag="div"
        className={`py-0 ${contentWrapperClassName}`}
        containerClassName="py-0"
        disableClip
      >
        <div
          className={`column ${narrowFrameColumns} ${verticalAlignmentContainer}`}
        >
          {content}
        </div>
      </Section>,
    );
  }

  if (headerFrame === "normal-frame" && contentFrame === "normal-frame") {
    headerAndContent = arrange(content);
  }
  if (headerFrame === "normal-frame" && contentFrame === "narrow-frame") {
    headerAndContent = arrange(
      <div className={`columns ${columns}`}>
        <div
          className={`column ${narrowFrameColumns} ${verticalAlignmentContainer}`}
        >
          {content}
        </div>
      </div>,
    );
  }

  if (headerFrame === "narrow-frame" && contentFrame === "narrow-frame") {
    headerAndContent = (
      <div
        className={`column ${narrowFrameColumns} is-relative ${verticalAlignmentContainer}`}
      >
        {arrange(content)}
      </div>
    );
  }

  const overlayClasses = `${arrangement === "overlay" ? `${styles["overlay"]} ${styles["overlay-depending-on-" + props.heightDependency]}` : ""}`;

  return (
    <Section
      tag="header"
      className={`${styles.header} ${className} ${headerFrame === "frameless" ? `py-0 ${styles["overlay-container"]}` : ""} is-relative ${styles[height]} ${overlayClasses}`}
      frameless={headerFrame === "frameless"}
      containerClassName={`py-0 is-relative ${headerFrame === "normal-frame" && contentFrame === "normal-frame" ? `${verticalAlignmentContainer} ${styles["overlay-container"]}` : ""}`}
      disableColumns={
        headerFrame !== "narrow-frame" || contentFrame !== "narrow-frame"
      }
      columnsClassName={columns}
      data-vertical-alignment={contentVerticalAlignment}
      disableClip
      style={
        {
          ...(max_height ? { "--max-height": `${max_height}dvh` } : {}),
        } as React.CSSProperties
      }
    >
      {headerAndContent}
    </Section>
  );
};

export default HeaderBase;
